import { v4 as uuid } from 'uuid';
import {
  createSelector,
  // createAsyncThunk,
  createSlice,
  PayloadAction,
  // createSelector,
} from '@reduxjs/toolkit';
import { toIsoDate } from '../utils/stringUtils';
import { Region } from '@/types';
import { priceFormatter, priceFormatterNoCents } from '../helpers/formatters';
import { filterChains } from './chains/ChainFilterForm';
import {
  ArrayElement,
  Market,
  OperationChain,
  PartialRecord,
  PointConstraint,
  PriceSource,
  Scenario,
  // ScenarioComponentSettingsAction,
  ScenarioComponentType,
  SegmentConstraint,
  Tag,
} from '@/types';
import {
  OptimizationType,
  PriceType,
  ScenarioComponentSettingsAction,
  ScenarioSettings,
} from '@/types/api/Scenario';

export type SortProperty =
  | 'NAME'
  | 'PROFIT'
  | 'IS_NEW'
  | 'PROFIT_DTH'
  | 'VOLUME';

export type ChainFilters = {
  ids: PartialRecord<string, boolean>;
  tsps: PartialRecord<string, boolean>;
  delivery: PartialRecord<string, boolean>;
  receipt: PartialRecord<string, boolean>;
  tags: PartialRecord<string, boolean>;
  negative: boolean;
  conjunctive: boolean;
};

export type ScenarioState = {
  isEditing: boolean;
  chainFilters: ChainFilters;
  sortChainsBy: SortProperty;
  pristineData: Scenario | null;
  pathFinderRegions: Region[];
  data: Scenario;
};

export const componentTypes: ScenarioComponentType[] = [
  'markets',
  'trades',
  'segment_constraints',
  'point_constraints',
  'contracts',
];

// ================================ Initial State ================================ //

const now = new Date();
const defaultDate = toIsoDate(now);
const defaultTime =
  ('0' + now.getHours()).slice(-2) + ('0' + now.getMinutes()).slice(-2);

const initialState: ScenarioState = {
  isEditing: false,
  chainFilters: {
    ids: {},
    tsps: {},
    delivery: {},
    receipt: {},
    tags: {},
    negative: false,
    conjunctive: false,
  },
  sortChainsBy: 'PROFIT',
  pristineData: null,
  pathFinderRegions: [],
  data: {
    id: 'new',
    name: `Scenario ${defaultDate} ${defaultTime}`,
    flow_date: defaultDate,
    optimize: true,
    multiCycle: false,
    trades: [],
    contracts: [],
    segment_constraints: [],
    point_constraints: [],
    markets: [],
    operation_chains: [],
    operations: [],
    locked: false,
    description: '',
    prev_in_group: null,
    next_in_group: null,
    previous_version: null,
    settings: {
      price_source: 'POSTMORTEM_DAILY',
      price_type: 'BID_ASK',
      simulate_spread: false,
      optimization_type: 'DEFAULT',
      optimization_settings: {
        delivery: null,
        receipt: [],
      },
      markets: {
        actions: [],
      },
      contracts: {
        actions: [],
      },
      trades: {
        actions: [],
      },
      segment_constraints: {
        actions: [],
      },
      point_constraints: {
        actions: [],
      },
    },
  },
};

// ================================ Private Methods ================================ //

// const _compareFunctions: {
//   [key in ScenarioComponentType]: (
//     a: ArrayElement<Scenario[key]>,
//     b: ArrayElement<Scenario[key]>,
//   ) => boolean;
// } = {
//   markets: (a, b) => a.point === b.point && a.buy_sell === b.buy_sell,
//   trades: (a, b) => a.point === b.point && a.buy_sell === b.buy_sell,
//   contracts: (a, b) => a.id === b.id,
//   segment_constraints: (a, b) => {
//     return a.component_key === b.component_key;
//   },
//   point_constraints: (a, b) => {
//     return a.component_key === b.component_key;
//   },
// };

const _validateComponentType = (componentType: string) =>
  componentTypes.includes(componentType as unknown as ScenarioComponentType);

const _findComponentIndex = (
  scenario: Scenario,
  componentType: ScenarioComponentType,
  componentId: string | number | undefined | null,
) => {
  if (_validateComponentType(componentType) === false) {
    console.error(`Invalid componentType: ${componentType}`);
    return null;
  }

  if (!componentId) {
    return -1;
  }

  const componentIndex = scenario[componentType].findIndex(
    (c) => c.id?.toString() === componentId.toString(),
  );

  return componentIndex;
};

// const applyAddSetting = <T extends ScenarioComponentType>(componentType: T, components: Scenario[T], action: ScenarioComponentSettingsAction<T>) => {
//   return [action.data, ...components];
// };

// const applyRemoveSetting = <T extends ScenarioComponentType>(componentType: T, components: Scenario[T], action: ScenarioComponentSettingsAction<T>) => {
//   let compare = _compareFunctions[componentType];
//   return components.filter((c) => compare(c, action.data) === false);
// };

// const _applyUpdateSetting = <T extends ScenarioComponentType>(componentType: T, components: Scenario[T], action: ScenarioComponentSettingsAction<T>) => {
//   let compare = _compareFunctions[componentType];
//   return components.map((c) => {
//     if (compare(c, action.data)) {
//       return { ...c, ...action.data };
//     }
//     return c;
//   });
// };

// ================================ Public Methods ================================ //

export const segmentConstraintKeyExtractor = (
  constraint: SegmentConstraint,
) => {
  return constraint.component_key;
};

export const pointConstraintKeyExtractor = (constraint: PointConstraint) => {
  return constraint.component_key;
};

export const marketKeyExtractor = (market: Market) => {
  return market.component_key;
};

export const chainSummary = (scenario: Scenario, chain: OperationChain) => {
  const chainOperations = chain.chain_links
    .filter((chain_link) => chain_link.has_flow == true)
    .map((chain_link) => {
      const operation = scenario.operations.find(
        (op) => op.id === chain_link.operation_id,
      );
      return {
        ...operation,
        chain_link,
      };
    });

  const receiptOp = chainOperations[0];
  const deliveryOp = chainOperations[chainOperations.length - 1];

  const receiptMarket =
    scenario.markets.find(
      (market) =>
        market.point.toString() === receiptOp.receipt_point?.id.toString() &&
        market.buy_sell !== 'SELL',
    ) ||
    scenario.trades.find((trade) => {
      return (
        trade.point.toString() === receiptOp.receipt_point?.id.toString() &&
        trade.buy_sell !== 'SELL'
      );
    });

  const deliveryMarket =
    scenario.markets.find(
      (market) =>
        market.point.toString() === deliveryOp.delivery_point?.id.toString() &&
        market.buy_sell !== 'BUY',
    ) ||
    scenario.trades.find((trade) => {
      return (
        trade.point.toString() === deliveryOp.delivery_point?.id.toString() &&
        trade.buy_sell !== 'BUY'
      );
    });

  let overallTransportCost = 0;

  const endpoints = [
    {
      Type: 'Receipt',
      Pipeline: receiptOp.tsp_name,
      Point: receiptOp.receipt_point?.name,
      'Loc ID': receiptOp.receipt_point?.loc_id,
      Operation: 'Buy',
      Market: receiptMarket?.description,
      Price: priceFormatter.format(receiptMarket?.price ?? 0),
    },
    {
      Type: 'Delivery',
      Pipeline: deliveryOp.tsp_name,
      Point: deliveryOp.delivery_point?.name,
      'Loc ID': deliveryOp.delivery_point?.loc_id,
      Operation: 'Sell',
      Market: deliveryMarket?.description,
      Price: priceFormatter.format(deliveryMarket?.price ?? 0),
    },
  ];
  const operations = chainOperations.map((op) => {
    const fuelCost = chain.fuel_makeup_price * ((op.fuel_factor ?? 0) - 1);
    const transportCost = Number(op.transport_cost_per_dth ?? 0) + fuelCost;
    overallTransportCost += transportCost;
    return {
      Receipt: `${op.receipt_point?.loc_id} - ${op.receipt_point?.name} - ${op.receipt_point?.receipt_zone_name}`,
      Delivery: `${op.delivery_point?.loc_id} - ${op.delivery_point?.name} - ${op.delivery_point?.delivery_zone_name}`,
      Pipeline: op.tsp_name,
      'Received Volume': op.chain_link.received_dth.toFixed(0),
      'Delivered Volume': op.chain_link.delivered_dth.toFixed(0),
      Priority: op.transport_priority,
      Contract: `${op.contract_name} (${op.contract_k_id})`,
      'Variable Cost': priceFormatter.format(op.transport_cost_per_dth ?? 0),
      'Fuel Cost': priceFormatter.format(fuelCost),
      'Transport Cost': priceFormatter.format(transportCost),
    };
  });
  const segmentConstraints = chainOperations.flatMap((op) => {
    return op.constraints
      ?.filter((constraint) => constraint.type === 'SEGMENT')
      .map(({ id: constraintId }) => {
        const constraint = scenario.segment_constraints.find(
          (constraint) => Number(constraint.id) === constraintId,
        );
        if (!constraint) return null;
        return {
          'Constraint Type': 'Segment',
          Description: constraint.short_description ?? constraint.description,
          // Add this back when we start using constraint_factor
          // 'Utilization Rate': `${constraint.constraint_factor * 100}%`,
          'Max Capacity': `${constraint.max_volume} dth`,
          Direction: constraint.flow_direction,
          'Cut Priority': constraint.cuts_at_priority,
        };
      })
      .filter((item) => item !== null);
  });
  const pointConstraints = chainOperations.flatMap((op) => {
    return op.constraints
      ?.filter((constraint) => constraint.type === 'POINT')
      .map(({ id: constraintId }) => {
        const constraint = scenario.point_constraints.find(
          (constraint) => Number(constraint.id) === constraintId,
        );
        if (!constraint) return null;
        return {
          'Constraint Type': 'Point',
          Description: constraint.short_description ?? constraint.description,
          // Add this back when we start using constraint_factor
          // 'Utilization Rate': `${constraint.constraint_factor * 100}%`,
          'Max Capacity': `${constraint.max_volume} dth`,
          Direction: constraint.flow_direction,
          'Cut Priority': constraint.cuts_at_priority,
        };
      })
      .filter((item) => item !== null);
  });
  const constraints = [...pointConstraints, ...segmentConstraints];
  const overview = {
    Profit: priceFormatterNoCents.format(Number(chain.profit)),
    Volume: (chain.delivered_dth ?? 0).toFixed(0),
    'Profit/Dth': priceFormatter.format(
      (chain.profit ?? 0) / (chain.delivered_dth ?? 1),
    ),
    'Total Transport Cost': priceFormatter.format(overallTransportCost),
  };

  return {
    overview,
    endpoints,
    operations,
    constraints,
  };
};

export const formatPriceSource = (price_source: PriceSource) => {
  switch (price_source) {
    case 'PLATTS_DAILY':
      return 'Platts Daily';
    case 'ICE_DAILY':
      return 'ICE Daily';
    case 'ICE_LIVE':
      return 'ICE Live';
    case 'USER_DEFINED':
      return 'User Defined';
    case 'MARK_TO_MARKET':
      return 'Mark to Market';
    case 'ICE_FUTURE':
      return 'ICE Future';
    case 'SIMULATED_SPREAD':
      return 'Simulated Spread';
    default:
      return price_source;
  }
};

// ================================ Reducers ================================ //

const resetState = (initialState: ScenarioState) => initialState;

const beginEditing = (state: ScenarioState, _action: PayloadAction) => {
  if (state.isEditing === true) {
    console.warn('State beginEdit called but isEditing is already true');
    return;
  }
  state.pristineData = state.data;
  state.isEditing = true;
};

const rollback = (state: ScenarioState, _action: PayloadAction) => {
  if (state.isEditing !== true) {
    console.warn('State rollback called but isEditing is not true');
    return;
  }
  state.data = state.pristineData ?? initialState.data;
  state.isEditing = false;
};

const commit = (state: ScenarioState, _action: PayloadAction) => {
  if (state.isEditing !== true) {
    console.warn('State commit called but isEditing is not true');
    return;
  }
  state.pristineData = null;
  state.isEditing = false;
};

// TODO: We must consider checking if the scenario is in edit mode before modifying the state
// maybe throw an error or simply return if not in edit mode

const overwrite = <T extends ScenarioComponentType>(
  state: ScenarioState,
  action: PayloadAction<{ componentType: T; components: Scenario[T] }>,
) => {
  const { componentType, components = [] } = action.payload;

  if (_validateComponentType(componentType) === false) {
    console.error(`Invalid componentType: ${componentType}`);
    return;
  }

  let targetComponents: Scenario[T] = components.map((scenarioComponent) => {
    if (!scenarioComponent.id) {
      return { ...scenarioComponent, id: uuid() };
    }
    return scenarioComponent;
  }) as Scenario[T];

  state.data[componentType] = targetComponents;
};

const setChainFilters = <T extends keyof ChainFilters>(
  state: ScenarioState,
  action: PayloadAction<{ type: T; value: ChainFilters[T] }>,
) => {
  const { type, value } = action.payload;
  state.chainFilters[type] = value;
};

const resetFilters = (state: ScenarioState, _action: PayloadAction) => {
  state.chainFilters = initialState.chainFilters;
};

const setScenario = (
  state: ScenarioState,
  action: PayloadAction<{ scenario: Scenario }>,
) => {
  const { scenario } = action.payload;
  state.data = { ...initialState.data, ...scenario };
  state.chainFilters = initialState.chainFilters;
};

const setName = (
  state: ScenarioState,
  action: PayloadAction<{ name: string }>,
) => {
  const { name } = action.payload;
  state.data.name = name;
};

const setDescription = (
  state: ScenarioState,
  action: PayloadAction<{ description: string }>,
) => {
  const { description } = action.payload;
  state.data.description = description;
};

const setPriceSource = (
  state: ScenarioState,
  action: PayloadAction<{ source: PriceSource }>,
) => {
  const { source } = action.payload;
  state.data.settings.price_source = source;
};

const setStrip = (
  state: ScenarioState,
  action: PayloadAction<{ strip: string }>,
) => {
  const { strip } = action.payload;
  state.data.settings.strip = strip;
};

const setPriceType = (
  state: ScenarioState,
  action: PayloadAction<{ priceType: PriceType }>,
) => {
  const { priceType } = action.payload;
  state.data.settings.price_type = priceType;
};

const setSimulateSpread = (
  state: ScenarioState,
  action: PayloadAction<{ simulateSpread: boolean }>,
) => {
  const { simulateSpread } = action.payload;
  state.data.settings.simulate_spread = simulateSpread;
};

const setFlowDate = (
  state: ScenarioState,
  action: PayloadAction<{ flow_date: string }>,
) => {
  const { flow_date } = action.payload;
  state.data.flow_date = flow_date;
};

const setOptimizationType = (
  state: ScenarioState,
  action: PayloadAction<{ optimizationType: OptimizationType }>,
) => {
  const { optimizationType } = action.payload;
  state.data.settings.optimization_type = optimizationType;
};

const setOptimizationSettings = (
  state: ScenarioState,
  action: PayloadAction<{
    optimizationSettings: ScenarioSettings['optimization_settings'];
  }>,
) => {
  state.data.settings.optimization_settings = {
    ...state.data.settings.optimization_settings,
    ...action.payload.optimizationSettings,
  };
};

/**
 * @brief Modifies the specified component
 */
const updateComponent = <T extends ScenarioComponentType>(
  state: ScenarioState,
  action: PayloadAction<{
    componentType: T;
    component: ArrayElement<Scenario[T]>;
  }>,
) => {
  const { componentType, component } = action.payload;

  const componentIndex = _findComponentIndex(
    state.data,
    componentType,
    component.id,
  );

  if (componentIndex === null) return;

  if (componentIndex === -1) {
    const id = component.id ? component.id : uuid();
    if (
      ['segment_constraints', 'point_constraints', 'markets'].includes(
        componentType,
      )
    ) {
      component.component_key =
        component.component_key && component.component_key.length > 0
          ? component.component_key
          : uuid();
    }
    state.data[componentType].unshift({
      ...component,
      // @ts-ignore
      id,
    });
    return;
  }

  state.data[componentType][componentIndex] = component;
};

const appendComponents = <T extends ScenarioComponentType>(
  state: ScenarioState,
  action: PayloadAction<{
    componentType: T;
    components: Scenario[T];
  }>,
) => {
  const { componentType } = action.payload;

  const components = action.payload.components.map((component) => {
    return {
      ...component,
      id: component.id ? component.id : uuid(),
    };
  }) as Scenario[T];

  // @ts-ignore
  state.data[componentType] = [...components, ...state.data[componentType]];
};

const removeComponent = <T extends ScenarioComponentType>(
  state: ScenarioState,
  action: PayloadAction<{
    componentType: T;
    componentId: string;
    saveSetting?: boolean;
  }>,
) => {
  const { componentType, componentId } = action.payload;

  const componentIndex = _findComponentIndex(
    state.data,
    componentType,
    componentId,
  );
  if (componentIndex === null) {
    console.error(`Invalid componentType: ${componentType}`);
    return;
  }

  if (componentIndex === -1) {
    console.warn(`Component ${componentId} not found in ${componentType}`);
    return;
  }

  const removedComponent = state.data[componentType].splice(
    componentIndex,
    1,
  )[0];
  state.data.settings[componentType].actions.push({
    // @ts-ignore
    type: 'REMOVE',
    // @ts-ignore
    data: removedComponent,
  });
};

/**
 *
 * @template {import('../types/api').ScenarioComponentType} T
 * @param {ScenarioState} state
 * @param {{
 *  payload: {
 *    componentType: T,
 *    componentIds: string[],
 *  }
 * }} action
 */
const restoreRemovedComponents = <T extends ScenarioComponentType>(
  state: ScenarioState,
  action: PayloadAction<{
    componentType: T;
    componentIds: string[];
  }>,
) => {
  const { componentType, componentIds } = action.payload;
  if (_validateComponentType(componentType) === false) {
    console.error(`Invalid componentType: ${componentType}`);
    return;
  }

  const [filteredActions, restoredElements] = state.data.settings[
    componentType
  ].actions.reduce<
    [ScenarioComponentSettingsAction<T>[], Partial<ArrayElement<Scenario[T]>>[]]
  >(
    (acc, current) => {
      if (
        current.type === 'REMOVE' &&
        componentIds.includes(current.data.id?.toString() ?? '')
      ) {
        // @ts-ignore
        acc[1].push(current.data);
      } else {
        // @ts-ignore
        acc[0].push(current);
      }
      return acc;
    },
    [[], []],
  );
  state.data.settings[componentType].actions = filteredActions;
  // @ts-ignore
  state.data[componentType].unshift(...restoredElements);
};

const bulkCreate = <T extends ScenarioComponentType>(
  state: ScenarioState,
  action: PayloadAction<{
    componentType: T;
    components: Scenario[T];
  }>,
) => {
  const { componentType, components } = action.payload;

  if (_validateComponentType(componentType) === false) {
    console.error(`Invalid componentType: ${componentType}`);
    return;
  }

  const newComponents = components
    .filter((component) => {
      // Filter out components that already exist
      return _findComponentIndex(state.data, 'contracts', component.id) === -1;
    })
    .map((component) => {
      return {
        ...component,
        id: component.id ? component.id : uuid(),
      };
    });

  // @ts-ignore
  state.data[componentType] = [...newComponents, ...state.data[componentType]];
};

const bulkUpdate = <T extends ScenarioComponentType>(
  state: ScenarioState,
  action: PayloadAction<{
    componentType: T;
    componentIds: string[];
    data: Partial<Scenario[T][0]>;
  }>,
) => {
  const { componentType, componentIds, data: updateData } = action.payload;

  if (_validateComponentType(componentType) === false) {
    console.error(`Invalid componentType: ${componentType}`);
    return;
  }

  const updatedComponents = state.data[componentType].map((component) => {
    if (!componentIds.includes(component.id?.toString() ?? '')) {
      return component;
    }
    const newComponent = { ...component, ...updateData };
    return newComponent;
  });

  // @ts-ignore
  state.data[componentType] = updatedComponents;
};

const bulkDelete = (
  state: ScenarioState,
  action: PayloadAction<{
    componentType: ScenarioComponentType;
    componentIds: string[];
  }>,
) => {
  const { componentType, componentIds } = action.payload;

  let deleteIds = componentIds.map((id) => id.toString());

  if (_validateComponentType(componentType) === false) {
    console.error(`Invalid componentType: ${componentType}`);
    return;
  }

  // @ts-ignore
  state.data[componentType] = state.data[componentType].filter((component) => {
    if (!deleteIds.includes(component.id?.toString() ?? '')) {
      return true;
    }

    state.data.settings[componentType].actions.push({
      type: 'REMOVE',
      // @ts-ignore
      data: component,
    });
    return false;
  });
};

const tagChain = (
  state: ScenarioState,
  action: PayloadAction<{
    chainInfoId: string;
    tag: Tag;
  }>,
) => {
  state.data.operation_chains = state.data.operation_chains.map((c) => {
    const chain = { ...c };
    if (
      chain.chain_info.id.toString() === action.payload.chainInfoId.toString()
    ) {
      chain.chain_info.tags.push(action.payload.tag);
    }
    return chain;
  });
};

const untagChain = (
  state: ScenarioState,
  action: PayloadAction<{
    chainInfoId: string;
    tag: Tag;
  }>,
) => {
  state.data.operation_chains = state.data.operation_chains.map((c) => {
    const chain = { ...c };
    if (
      chain.chain_info.id.toString() === action.payload.chainInfoId.toString()
    ) {
      chain.chain_info.tags = chain.chain_info.tags.filter(
        (t) => t.id.toString() !== action.payload.tag.id.toString(),
      );
    }
    return chain;
  });
};

const updateChainName = (
  state: ScenarioState,
  action: PayloadAction<{
    chainInfoId: string;
    name: string;
  }>,
) => {
  state.data.operation_chains = state.data.operation_chains.map((c) => {
    const chain = { ...c };
    if (
      chain.chain_info.id.toString() === action.payload.chainInfoId.toString()
    ) {
      chain.chain_info.name = action.payload.name;
    }
    return chain;
  });
};

const setLockState = (
  state: ScenarioState,
  action: PayloadAction<{
    locked: boolean;
  }>,
) => {
  state.data.locked = action.payload.locked;
};

const setSortProperty = (
  state: ScenarioState,
  action: PayloadAction<{
    sortBy: SortProperty;
  }>,
) => {
  state.sortChainsBy = action.payload.sortBy;
};

const setPathFinderRegions = (
  state: ScenarioState,
  action: PayloadAction<{
    pathFinderRegions: Region[];
  }>,
) => {
  state.pathFinderRegions = action.payload.pathFinderRegions;
};

/**
 *
 * @param {ScenarioState} initialState
 */
const _scenarioReducers = (initialState: ScenarioState) => ({
  resetState: () => resetState(initialState),
  beginEditing,
  rollback,
  commit,
  overwrite,
  setChainFilters,
  resetFilters,
  setScenario,
  setName,
  setDescription,
  setFlowDate,
  setPriceSource,
  setStrip,
  setPriceType,
  setSimulateSpread,
  setOptimizationType,
  setOptimizationSettings,
  updateComponent,
  appendComponents,
  removeComponent,
  delete: removeComponent, // alias
  restoreRemovedComponents,
  bulkCreate,
  bulkUpdate,
  bulkDelete,
  tagChain,
  untagChain,
  updateChainName,
  setLockState,
  setSortProperty,
  setPathFinderRegions,
});

const reducers = _scenarioReducers(initialState);

export const scenarioSlice = createSlice<
  ScenarioState,
  typeof reducers,
  'scenario'
>({
  name: 'scenario',
  initialState,
  reducers,
});

export const scenarioActions = scenarioSlice.actions;

export const selectScenario = createSelector(
  (state: { scenario: ScenarioState }) => state?.scenario.data ?? {},
  (scenario) => scenario,
);

export const selectFlowDate = createSelector(
  (state: { scenario: ScenarioState }) => state?.scenario.data?.flow_date,
  (flow_date) => flow_date,
);

export const selectMarkets = createSelector(
  (state: { scenario: ScenarioState }) => state?.scenario.data ?? {},
  (scenario) => scenario.markets ?? [],
);

export const selectTrades = createSelector(
  (state: { scenario: ScenarioState }) => state?.scenario.data ?? {},
  (scenario) => scenario.trades ?? [],
);

export const selectSegmentConstraints = createSelector(
  (state: { scenario: ScenarioState }) => state?.scenario?.data ?? {},
  (scenario) => scenario.segment_constraints ?? [],
);

export const selectPointConstraints = createSelector(
  (state: { scenario: ScenarioState }) => state?.scenario?.data ?? {},
  (scenario) => scenario.point_constraints ?? [],
);

export const selectScenarioContracts = createSelector(
  (state: { scenario: ScenarioState }) => state?.scenario?.data ?? {},
  (scenario) => scenario?.contracts ?? [],
);

export const selectConstraintsAtPoints = createSelector(
  selectPointConstraints,
  (constraints) => {
    return constraints.reduce<PartialRecord<string, PointConstraint[]>>(
      (
        acc: PartialRecord<string, PointConstraint[]>,
        constraint: PointConstraint,
      ) => {
        if (!acc[constraint.point]) {
          acc[constraint.point] = [];
        }
        acc[constraint.point]?.push(constraint);
        return acc;
      },
      {},
    );
  },
);

export const selectPriceSource = createSelector(
  (state: { scenario: ScenarioState }) =>
    state?.scenario?.data?.settings?.price_source,
  (priceSource) => priceSource,
);

export const selectPriceType = createSelector(
  (state: { scenario: ScenarioState }) =>
    state?.scenario?.data?.settings?.price_type,
  (priceType) => priceType,
);

export const selectOptimizationType = createSelector(
  (state: { scenario: ScenarioState }) =>
    state?.scenario?.data?.settings?.optimization_type,
  (optimizationType) => optimizationType,
);

export const selectSimulatedSpread = createSelector(
  (state: { scenario: ScenarioState }) =>
    state?.scenario?.data?.settings?.simulate_spread,
  (simulateSpread) => simulateSpread,
);

export const selectStrip = createSelector(
  (state: { scenario: ScenarioState }) =>
    state?.scenario?.data?.settings?.strip,
  (strip) => strip,
);

export const selectSegmentConstraintSettings = createSelector(
  (state: { scenario: ScenarioState }) =>
    state?.scenario?.data?.settings?.segment_constraints,
  (actions) => actions,
);

export const selectDeletedSegmentConstraints = createSelector(
  selectSegmentConstraintSettings,
  (segmentConstraintSettings) =>
    segmentConstraintSettings.actions
      ?.filter((action) => action.type === 'REMOVE')
      .map((action) => action.data) ?? [],
);

export const selectPointConstraintSettings = createSelector(
  (state: { scenario: ScenarioState }) =>
    state?.scenario?.data?.settings?.point_constraints,
  (actions) => actions,
);

export const selectDeletedPointConstraints = createSelector(
  selectPointConstraintSettings,
  (pointConstraintSettings) =>
    pointConstraintSettings.actions
      ?.filter((action) => action.type === 'REMOVE')
      .map((action) => action.data) ?? [],
);

export const selectMarketSettings = createSelector(
  (state: { scenario: ScenarioState }) =>
    state?.scenario?.data?.settings?.markets,
  (actions) => actions,
);

export const selectDeletedMarkets = createSelector(
  selectMarketSettings,
  (marketSettings) =>
    marketSettings.actions
      ?.filter((action) => action.type === 'REMOVE')
      .map((action) => action.data) ?? [],
);

export const selectTradeSettings = createSelector(
  (state: { scenario: ScenarioState }) =>
    state?.scenario?.data?.settings?.trades,
  (actions) => actions,
);

export const selectDeletedTrades = createSelector(
  selectTradeSettings,
  (tradeSettings) =>
    tradeSettings.actions
      ?.filter((action) => action.type === 'REMOVE')
      .map((action) => action.data) ?? [],
);

export const selectOperations = createSelector(
  (state: { scenario: ScenarioState }) => state?.scenario.data ?? {},
  (scenario) => scenario?.operations ?? [],
);

export const selectEditState = createSelector(
  (state: { scenario: ScenarioState }) => state?.scenario?.isEditing ?? false,
  (isEditing) => isEditing,
);

export const selectChainFilters = createSelector(
  (state: { scenario: ScenarioState }) =>
    state?.scenario?.chainFilters ?? initialState.chainFilters,
  (chainFilters) => chainFilters,
);

export const selectFilteredChains = createSelector(
  [selectScenario, selectChainFilters],
  (scenario, chainFilters) => {
    return filterChains(scenario, scenario.operation_chains, chainFilters);
  },
);

export const selectSortProperty = createSelector(
  (state: { scenario: ScenarioState }) =>
    state?.scenario?.sortChainsBy ?? initialState.sortChainsBy,
  (sortChainsBy) => sortChainsBy,
);

export const selectSortedChains = createSelector(
  [selectFilteredChains, selectSortProperty],
  (chains, sortProperty) => {
    const propertyToUse: Record<SortProperty, keyof OperationChain> = {
      // @ts-ignore
      NAME: 'chain_info.name',
      PROFIT: 'profit',
      IS_NEW: 'is_new',
      VOLUME: 'delivered_dth',
      PROFIT_DTH: 'profit', // special case handled below
    };
    return chains.slice().sort((a, b) => {
      if (sortProperty === 'PROFIT_DTH') {
        return b.profit / b.delivered_dth - a.profit / a.delivered_dth;
      }
      if (sortProperty === 'NAME' && (!a.chain_info || !b.chain_info)) {
        return 0;
      }
      return (
        Number(b[propertyToUse[sortProperty]]) -
        Number(a[propertyToUse[sortProperty]])
      );
    });
  },
);

export const selectPathFinderRegions = createSelector(
  (state: { scenario: ScenarioState }) =>
    state?.scenario?.pathFinderRegions ?? initialState.pathFinderRegions,
  (pathFinderRegions) => pathFinderRegions ?? [],
);

export default scenarioSlice.reducer;
